// actions
import * as Actions from '../actions';
import { AppState } from '../reducers';
import { ActionCreators as AzureFileActions } from './AzureFileUpload.Middleware';

// services
import * as Services from '../../services/CashFlow-CreditorInvoices.Services';

// functions
import { buildPaymentReceiptTarget, buildPractitionerInvoiceTarget } from '../../functions/AzureFileUpload.Functions';
import { settlementInvoiceCandidates } from '../../functions/CreditorInvoices.Functions';
import { isNotFound } from '../../functions/PractitionerAccountStatement.Functions';
import { creditorKey } from '../states/CreditorInvoicesState';

// models
import {
    CreditorInvoice,
    CreditorInvoiceData,
    CreditorInvoiceSourceEnum,
    PaymentAttachmentsOutcome,
    PaymentAttachmentsRequest,
    PaymentInvoiceAllocation,
    PaymentInvoiceAllocationInput,
    PaymentReceiptUploadOutcome,
    SetPaymentInvoicesResult,
    SettlementInvoiceCandidate
} from '../../models/Entities/CashFlow/CreditorInvoice/CreditorInvoice';
import { AzureFileUploadResult } from '../../models/Entities/AzureFiles/AzureFileUpload';
import { AzureFileTypeEnum } from '../../models/Enums/AzureFileTypeEnum';
import { ErrorType } from '../../models/HttpError';

/**
 * Facturas del profesional y comprobantes de pago (contrato §12). El token se lee
 * sólo acá. Los thunks devuelven promesas para que las pantallas sepan el resultado
 * (y ofrezcan "Reintentar" sin perder lo que ya se guardó).
 */

type KnownAction = Actions.CreditorInvoices.CreditorInvoicesKnownAction | Actions.AzureFileUpload.AzureFileKnownAction;

export type CreditorInvoicesThunk<TResult> = (dispatch: (action: any) => void, getState: () => AppState) => Promise<TResult>;

const NotAuthorized: ErrorType = { ErrorCode: 401, ErrorMessage: 'Not authorized', Errors: [] };

const toErrorType = (error: unknown): ErrorType => {
    if (typeof error === 'object' && error !== null && 'ErrorCode' in error) return error as ErrorType;
    return { ErrorCode: 0, ErrorMessage: error instanceof Error ? error.message : 'Unexpected error', Errors: [] };
};

const tokenOf = (getState: () => AppState): string | undefined => getState().oidc?.user?.access_token;

const requireToken = (getState: () => AppState): string => {
    const token = tokenOf(getState);
    if (!token) throw NotAuthorized;
    return token;
};

/** Lee las facturas y actualiza el store. Nunca rechaza: el estado queda en el store. */
const loadInvoices = async (dispatch: (action: KnownAction) => void, token: string | undefined, creditorId: string): Promise<CreditorInvoice[] | undefined> => {
    if (!token) {
        dispatch({ type: 'CREDITOR_INVOICES_FAIL_ACTION', creditorId: creditorKey(creditorId), error: NotAuthorized });
        return undefined;
    }
    dispatch({ type: 'CREDITOR_INVOICES_REQUEST_ACTION', creditorId: creditorKey(creditorId) });
    try {
        const invoices = await Services.GetCreditorInvoices(token, creditorId);
        dispatch({ type: 'CREDITOR_INVOICES_RECEIVE_ACTION', creditorId: creditorKey(creditorId), invoices });
        return invoices;
    } catch (error: unknown) {
        if (isNotFound(error)) {
            dispatch({ type: 'CREDITOR_INVOICES_UNAVAILABLE_ACTION', creditorId: creditorKey(creditorId) });
            return undefined;
        }
        dispatch({ type: 'CREDITOR_INVOICES_FAIL_ACTION', creditorId: creditorKey(creditorId), error: toErrorType(error) });
        return undefined;
    }
};

const invoiceTargetOf = (creditorId: string) => {
    const target = buildPractitionerInvoiceTarget(creditorId);
    if (!target) throw { ErrorCode: 400, ErrorMessage: 'El identificador del profesional no es válido.', Errors: [] } as ErrorType;
    return target;
};

const registerBody = (data: CreditorInvoiceData): Record<string, unknown> => ({ source: CreditorInvoiceSourceEnum.Uploaded, ...data });

/** Sube los comprobantes (tipo 9) del pago y los registra en CashFlow. Nunca rechaza. */
const uploadReceipts = async (
    dispatch: (action: any) => void,
    getState: () => AppState,
    paymentId: number,
    creditorId: string,
    files: File[]
): Promise<PaymentReceiptUploadOutcome[]> => {
    const target = buildPaymentReceiptTarget(paymentId, creditorId);
    const outcomes: PaymentReceiptUploadOutcome[] = [];
    // Uno por vez: el progreso y los errores quedan claros por archivo.
    for (const file of files) {
        if (!target) {
            outcomes.push({ fileName: file.name, file, status: 'failed', error: { ErrorCode: 400, ErrorMessage: 'Pago o profesional inválido.', Errors: [] } });
            continue;
        }
        try {
            const result: AzureFileUploadResult = await AzureFileActions.UploadAzureFile({ file, fileType: AzureFileTypeEnum.PaymentReceipt, target })(dispatch, getState);
            outcomes.push({
                fileName: file.name,
                file,
                status: result.registered === false ? 'unregistered' : 'uploaded',
                relativePath: result.relativePath,
                error: result.registerError
            });
        } catch (error: unknown) {
            outcomes.push({ fileName: file.name, file, status: 'failed', error: toErrorType(error) });
        }
    }
    return outcomes;
};

const saveAllocations = async (
    dispatch: (action: KnownAction) => void,
    token: string,
    paymentId: number,
    allocations: PaymentInvoiceAllocationInput[]
): Promise<SetPaymentInvoicesResult> => {
    const result = await Services.SetPaymentInvoices(token, paymentId, allocations);
    dispatch({ type: 'PAYMENT_INVOICES_COUNT_ACTION', paymentId, count: result.allocations.length || allocations.length });
    return result;
};

export const actionCreators = {
    /** Facturas del profesional. 404 → "dato no disponible todavía". Nunca rechaza. */
    LoadCreditorInvoices: (creditorId: string): CreditorInvoicesThunk<CreditorInvoice[] | undefined> =>
        async (dispatch, getState) => loadInvoices(dispatch, tokenOf(getState), creditorId),

    /**
     * "+ Subir factura": sube el archivo a Azure (tipo 8, entidad Practitioner con el GUID, titular el
     * profesional) y lo registra en CashFlow con sus datos (source 1). Rechaza si la subida falla; si
     * sólo falla el registro, resuelve con `registered: false` y el archivo queda subido para reintentar.
     */
    UploadCreditorInvoice: (creditorId: string, file: File, data: CreditorInvoiceData, onProgress?: (percent: number) => void): CreditorInvoicesThunk<AzureFileUploadResult> =>
        async (dispatch, getState) => {
            requireToken(getState);
            const target = invoiceTargetOf(creditorId);
            const result = await AzureFileActions.UploadAzureFile(
                { file, fileType: AzureFileTypeEnum.PractitionerInvoice, target, registerExtra: registerBody(data) },
                onProgress
            )(dispatch, getState);
            if (result.registered !== false) {
                dispatch({ type: 'CREDITOR_INVOICES_SAVED_ACTION', creditorId: creditorKey(creditorId), operation: 'create' });
            }
            await loadInvoices(dispatch, tokenOf(getState), creditorId);
            return result;
        },

    /** Reintenta sólo el registro de una factura ya subida. Rechaza con ErrorType. */
    RetryRegisterCreditorInvoice: (creditorId: string, path: string, data: CreditorInvoiceData): CreditorInvoicesThunk<void> =>
        async (dispatch, getState) => {
            requireToken(getState);
            await AzureFileActions.RegisterAzureFile('creditorInvoice', invoiceTargetOf(creditorId), path, registerBody(data))(dispatch, getState);
            dispatch({ type: 'CREDITOR_INVOICES_SAVED_ACTION', creditorId: creditorKey(creditorId), operation: 'create' });
            await loadInvoices(dispatch, tokenOf(getState), creditorId);
        },

    /** "Traer facturas de liquidaciones": registra una factura de liquidación (source 2). Rechaza con ErrorType. */
    RegisterSettlementInvoice: (creditorId: string, candidate: SettlementInvoiceCandidate, data: CreditorInvoiceData): CreditorInvoicesThunk<CreditorInvoice | undefined> =>
        async (dispatch, getState) => {
            const token = requireToken(getState);
            try {
                const invoice = await Services.CreateCreditorInvoice(token, creditorId, {
                    ...data,
                    path: candidate.path,
                    source: CreditorInvoiceSourceEnum.SettlementFile,
                    settlementId: candidate.settlementId,
                    settlementFileId: candidate.settlementFileId
                });
                dispatch({ type: 'CREDITOR_INVOICES_SAVED_ACTION', creditorId: creditorKey(creditorId), operation: 'create', invoiceId: invoice?.id });
                return invoice;
            } catch (error: unknown) {
                throw toErrorType(error);
            } finally {
                await loadInvoices(dispatch, token, creditorId);
            }
        },

    /** Facturas de las liquidaciones del profesional que todavía no están registradas. Rechaza con ErrorType. */
    GetSettlementInvoiceCandidates: (creditorId: string, settlementIds: number[]): CreditorInvoicesThunk<{ candidates: SettlementInvoiceCandidate[]; failedSettlementIds: number[] }> =>
        async (dispatch, getState) => {
            const token = requireToken(getState);
            const known = getState().CreditorInvoices?.byCreditor?.[creditorKey(creditorId)]?.data;
            const invoices = known ?? (await loadInvoices(dispatch, token, creditorId)) ?? [];
            try {
                const { files, failedSettlementIds } = await Services.GetSettlementsInvoiceFiles(token, settlementIds);
                return { candidates: settlementInvoiceCandidates(files, invoices), failedSettlementIds };
            } catch (error: unknown) {
                throw toErrorType(error);
            }
        },

    /** "Editar datos". Rechaza con ErrorType (409: no se puede cambiar la moneda de una factura con pagos). */
    UpdateCreditorInvoice: (creditorId: string, invoiceId: number, data: CreditorInvoiceData): CreditorInvoicesThunk<CreditorInvoice | undefined> =>
        async (dispatch, getState) => {
            const token = requireToken(getState);
            try {
                const invoice = await Services.UpdateCreditorInvoice(token, creditorId, invoiceId, data);
                dispatch({ type: 'CREDITOR_INVOICES_SAVED_ACTION', creditorId: creditorKey(creditorId), operation: 'update', invoiceId });
                await loadInvoices(dispatch, token, creditorId);
                return invoice;
            } catch (error: unknown) {
                throw toErrorType(error);
            }
        },

    /** "Eliminar". 409 = tiene pagos asociados (la pantalla ofrece "Eliminar igual" → force). Rechaza con ErrorType. */
    DeleteCreditorInvoice: (creditorId: string, invoiceId: number, force = false): CreditorInvoicesThunk<void> =>
        async (dispatch, getState) => {
            const token = requireToken(getState);
            try {
                await Services.DeleteCreditorInvoice(token, creditorId, invoiceId, force);
                dispatch({ type: 'CREDITOR_INVOICES_SAVED_ACTION', creditorId: creditorKey(creditorId), operation: 'delete', invoiceId });
                await loadInvoices(dispatch, token, creditorId);
            } catch (error: unknown) {
                throw toErrorType(error);
            }
        },

    /** Facturas que cubre un pago. Rechaza con ErrorType (404 = todavía no disponible). */
    GetPaymentInvoices: (paymentId: number): CreditorInvoicesThunk<PaymentInvoiceAllocation[]> =>
        async (dispatch, getState) => {
            const token = requireToken(getState);
            try {
                const allocations = await Services.GetPaymentInvoices(token, paymentId);
                dispatch({ type: 'PAYMENT_INVOICES_COUNT_ACTION', paymentId, count: allocations.length });
                return allocations;
            } catch (error: unknown) {
                throw toErrorType(error);
            }
        },

    /** Reemplaza las facturas del pago y devuelve los avisos. Rechaza con ErrorType. */
    SetPaymentInvoices: (paymentId: number, allocations: PaymentInvoiceAllocationInput[], creditorId?: string): CreditorInvoicesThunk<SetPaymentInvoicesResult> =>
        async (dispatch, getState) => {
            const token = requireToken(getState);
            try {
                return await saveAllocations(dispatch, token, paymentId, allocations);
            } catch (error: unknown) {
                throw toErrorType(error);
            } finally {
                if (creditorId) await loadInvoices(dispatch, token, creditorId);
            }
        },

    /**
     * Después de crear el pago: asocia las facturas (PUT payments/{id}/invoices) y sube + registra los
     * comprobantes (tipo 9, entidad Payment con el id nuevo, titular el profesional). Nunca rechaza: el pago
     * ya existe, el resultado dice qué falló para ofrecer "Reintentar".
     */
    AttachToPayment: (request: PaymentAttachmentsRequest): CreditorInvoicesThunk<PaymentAttachmentsOutcome> =>
        async (dispatch, getState) => {
            const outcome: PaymentAttachmentsOutcome = { paymentId: request.paymentId, receipts: [] };
            const token = tokenOf(getState);
            if (request.allocations && request.allocations.length > 0) {
                try {
                    if (!token) throw NotAuthorized;
                    const result = await saveAllocations(dispatch, token, request.paymentId, request.allocations);
                    outcome.allocations = { status: 'saved', warnings: result.warnings };
                } catch (error: unknown) {
                    outcome.allocations = { status: 'failed', warnings: [], error: toErrorType(error) };
                }
            }
            if (request.receipts.length > 0) {
                outcome.receipts = await uploadReceipts(dispatch, getState, request.paymentId, request.creditorId, request.receipts);
            }
            if (request.allocations && request.allocations.length > 0) await loadInvoices(dispatch, token, request.creditorId);
            return outcome;
        },

    /**
     * Reintenta sólo lo que falló de `AttachToPayment`: las facturas si no se guardaron, los comprobantes que
     * no se subieron (se vuelven a subir) y los que se subieron sin registrar (sólo el registro). Nunca rechaza.
     */
    RetryPaymentAttachments: (request: PaymentAttachmentsRequest, previous: PaymentAttachmentsOutcome): CreditorInvoicesThunk<PaymentAttachmentsOutcome> =>
        async (dispatch, getState) => {
            const token = tokenOf(getState);
            const outcome: PaymentAttachmentsOutcome = { paymentId: request.paymentId, allocations: previous.allocations, receipts: [] };
            if (previous.allocations?.status === 'failed' && request.allocations && request.allocations.length > 0) {
                try {
                    if (!token) throw NotAuthorized;
                    const result = await saveAllocations(dispatch, token, request.paymentId, request.allocations);
                    outcome.allocations = { status: 'saved', warnings: result.warnings };
                } catch (error: unknown) {
                    outcome.allocations = { status: 'failed', warnings: [], error: toErrorType(error) };
                }
            }
            const target = buildPaymentReceiptTarget(request.paymentId, request.creditorId);
            for (const item of previous.receipts) {
                if (item.status === 'uploaded') {
                    outcome.receipts.push(item);
                } else if (item.status === 'unregistered' && item.relativePath && target) {
                    try {
                        await AzureFileActions.RegisterAzureFile('paymentReceipt', target, item.relativePath)(dispatch, getState);
                        outcome.receipts.push({ ...item, status: 'uploaded', error: undefined });
                    } catch (error: unknown) {
                        outcome.receipts.push({ ...item, status: 'unregistered', error: toErrorType(error) });
                    }
                } else {
                    const [again] = await uploadReceipts(dispatch, getState, request.paymentId, request.creditorId, [item.file]);
                    outcome.receipts.push(again);
                }
            }
            if (previous.allocations?.status === 'failed') await loadInvoices(dispatch, token, request.creditorId);
            return outcome;
        }
};

export { hasAttachmentFailures } from '../../functions/CreditorInvoices.Functions';
